import React, { useRef } from 'react';
import uploadAttachmentHandler from './UploadAttachmentHandler';

// Check drag support once — avoids repeated DOM calls on every render
const supportsDragDrop = 'draggable' in document.createElement('span');

/**
 * Dropzone — handles drag, click and paste for file selection.
 * Shows image preview of the currently selected file when available.
 * @param {UploadFileItem|null} selectedItem - currently selected file item
 * @param {string} defaultProvider - passed to handler on file add
 * @param {boolean} locked - true during upload, disables interaction
 * @param {boolean} isDragging - drag-over state for visual feedback
 * @param {Function} onDragChange - called with true/false on drag enter/leave
 */
export default function UploadDropzone({ selectedItem, defaultProvider, locked, isDragging, onDragChange }) {
    const fileInputRef = useRef(null);

    /**
     * Handles file drop on the dropzone.
     * @param {DragEvent} e
     */
    const handleDrop = (e) => {
        e.preventDefault();
        if (locked) return;
        onDragChange(false);
        const { files } = e.dataTransfer;
        if (files?.length) {
            uploadAttachmentHandler.dispatch('onFilesAdded', { fileList: files, defaultProvider });
        }
    };

    /**
     * Handles file selection via hidden file input.
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const handleFileInput = (e) => {
        if (locked) return;
        const { files } = e.target;
        if (files?.length) {
            uploadAttachmentHandler.dispatch('onFilesAdded', { fileList: files, defaultProvider });
        }
        // Reset input value so the same file can be re-added after removal
        e.target.value = '';
    };

    /**
     * Returns dropzone label based on device capability and current file state.
     */
    const getLabel = () => {
        if (selectedItem) return selectedItem.fileName;
        if (supportsDragDrop) return 'Drag, paste or click to choose files';
        return 'Tap to choose files';
    };

    return (
        <div id="upload-dropzone-root" className="upload-dropzone-root">
            <div
                id="upload-dlg-dropzone"
                className={`upload-dlg-dropzone ${isDragging ? 'dragging' : ''} ${selectedItem ? 'has-file' : ''} ${locked ? 'locked' : ''}`}
                onDragOver={(e) => { e.preventDefault(); if (!locked) onDragChange(true); }}
                onDragLeave={() => onDragChange(false)}
                onDrop={handleDrop}
                onClick={() => { if (!locked) fileInputRef.current?.click(); }}
            >
                {getLabel()}
            </div>

            {/* Image preview — only visible when selected item is an image */}
            {selectedItem?.previewUrl && (
                <img
                    id="upload-dlg-preview"
                    className="upload-dlg-preview"
                    src={selectedItem.previewUrl}
                    alt="preview"
                />
            )}

            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="upload-dlg-hidden-input"
                onChange={handleFileInput}
            />
        </div>
    );
}
